import { Box, Button, Dialog } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import EmlViewerFromApi from 'pages/common/EmlViewer';
import React, { useState } from 'react';
import {
  useFetchList,
  usePatchItem,
} from 'services/common/react-query-common-service';
import { useAppSelector } from 'store/hooks';
import { SALES_ENDPOINT } from 'utils/Constant';

import CandidateMatchScoresScreen from '../../candidate-match-scores';
import { JobQueueTableType, TeamUserRoles } from '../../enum/TeamUserRoles';
import ActivityLog from './ActivityLog';
import ApprovedCandidates from './ApprovedCandidates';
import JobDescriptionContent from './JobDescriptionContent';
import ReadyForReviewCandidates from './ReadyForReviewCandidates';
import SubmitCandidatesPopup from './SubmitCandidatesPopup';
import SubmittedCandidates from './SubmittedCandidates';

interface JobContentProps {
  jobInfo: any;
  tableType: JobQueueTableType;
  refetch?: any;
  score?: string;
}

const JobContent: React.FC<JobContentProps> = ({
  jobInfo,
  tableType,
  refetch,
  score,
}) => {
  const { userInfo: userDetail }: any = useAppSelector(
    (store: any) => store.userLogin,
  );
  const [activeTab, setActiveTab] = useState('Job Description');
  const [selectedCandidates, setSelectedCandidates] = useState<
    Map<string, any>
  >(new Map());
  const [showSubmitPopup, setShowSubmitPopup] = useState(false);
  const queryClient = useQueryClient();

  const { mutate: patchCandidates, isPending: isUpdating } = usePatchItem();
  const [openScoreModal, setOpenScoreModal] = useState(false);
  const [selectedScoreCandidateId, setSelectedScoreCandidateId] = useState<
    string | null
  >(null);

  const jobId = jobInfo?.id;

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setSelectedCandidates(new Map());
  };

  const toggleSelection = (id: string, item: any) => {
    setSelectedCandidates((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, item);
      return next;
    });
  };

  const handleOpenCandidateModal = (candidateId: string) => {
    setSelectedScoreCandidateId(candidateId);
    setOpenScoreModal(true);
  };

  // Fetch submitted candidates data when Submitted tab is active
  const { data: submittedCandidatesData, isLoading: isLoadingSubmitted } =
    useFetchList<any>(
      ['submitted-candidates', jobId, score],
      `${
        SALES_ENDPOINT.MatchedCandidates
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      }/${jobId}?type=submitted&sourceQueue=jobQueue&score=${String(
        score || '0',
      )}`,
      {
        enabled: !!jobId && activeTab === 'Submitted List',
        refetchOnMount: 'always',
      },
    );

  const handleAction = () => {
    const candidateIds = Array.from(selectedCandidates.values()).map(
      (item) => item.candidate?.id,
    );
    const endpointSuffix = 'approve';

    patchCandidates(
      {
        data: {
          candidateId: candidateIds,
          isDownloadable: false,
          sourceQueue: 'jobQueue',
        },
        endpoint: `${String(SALES_ENDPOINT.MatchedCandidates)}/${String(
          endpointSuffix,
        )}/${String(jobId)}`,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ['ready-for-review-candidates', jobId],
          });
          setShowSubmitPopup(false);
          setSelectedCandidates(new Map());
        },
      },
    );
  };

  return (
    <>
      <Box className="section-tabs">
        {(() => {
          let tabs = [];
          if (tableType === JobQueueTableType.TEAM_LEAD_READY_FOR_REVIEW) {
            tabs = [
              'Job Description',
              'Activity Log',
              'Ready for Review',
              'Approved',
            ];
          } else if (
            [
              userDetail?.teamUserRole === TeamUserRoles.MANAGER,
              userDetail?.teamUserRole === TeamUserRoles.MANAGEMENT,
              userDetail?.teamUserRole === TeamUserRoles.BUSINESS_HEAD,
            ].includes(true) &&
            (tableType === JobQueueTableType.MANAGER_SUBMITTED ||
              tableType === JobQueueTableType.BUSINESS_HEAD_SUBMITTED ||
              tableType === JobQueueTableType.MANAGEMENT_JQ_SUBMITTED)
          ) {
            tabs = ['Job Description', 'Activity Log', 'Submitted List'];
          } else if (
            [
              userDetail?.teamUserRole === TeamUserRoles.MANAGER,
              userDetail?.teamUserRole === TeamUserRoles.MANAGEMENT,
              userDetail?.teamUserRole === TeamUserRoles.BUSINESS_HEAD,
              userDetail?.teamUserRole === TeamUserRoles.TEAM_LEADER,
            ].includes(true)
          ) {
            tabs = ['Job Description', 'Activity Log'];
          } else if (
            tableType === JobQueueTableType.SUPPORT_SUBMITTED ||
            tableType === JobQueueTableType.RECRUITER_SUBMITTED
          ) {
            tabs = ['Job Description', 'Submitted List'];
          } else {
            tabs = ['Job Description'];
          }

          const rawSource =
            typeof jobInfo?.jobSource === 'string'
              ? jobInfo.jobSource
              : jobInfo?.jobSource?.name || '';
          const source = rawSource.toLowerCase();

          if (source === 'email') {
            tabs.splice(1, 0, 'Original Email');
          }
          return tabs;
        })().map((tab) => (
          <Box
            key={tab}
            className={`tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => handleTabChange(tab)}>
            {tab}
          </Box>
        ))}
      </Box>

      <Box className="job-content">
        {activeTab === 'Job Description' && (
          <JobDescriptionContent jobInfo={jobInfo} />
        )}

        {activeTab === 'Ready for Review' && (
          <ReadyForReviewCandidates
            score={score}
            jobId={jobId}
            tableType={tableType}
            selectedIds={new Set(selectedCandidates.keys())}
            onToggleSelection={toggleSelection}
          />
        )}

        {activeTab === 'Approved' && (
          <ApprovedCandidates
            score={score}
            jobId={jobId}
            tableType={tableType}
          />
        )}

        {activeTab === 'Activity Log' && <ActivityLog jobId={jobId} />}

        {activeTab === 'Submitted List' && (
          <SubmittedCandidates
            score={score}
            jobId={jobId}
            tableType={tableType}
            selectedIds={new Set(selectedCandidates.keys())}
            onToggleSelection={toggleSelection}
            onOpenCandidateModal={handleOpenCandidateModal}
            data={submittedCandidatesData}
            isLoading={isLoadingSubmitted}
          />
        )}

        {activeTab === 'Original Email' && (
          <EmlViewerFromApi
            jobId={jobId}
            setEmailDataContent={() => {}}
            source="email"
          />
        )}
      </Box>

      {selectedCandidates.size > 0 && activeTab === 'Ready for Review' && (
        <Box className="selection-footer">
          <Button
            className="submit-btn"
            variant="contained"
            onClick={() => setShowSubmitPopup(true)}>
            Mark as Approved ({selectedCandidates.size})
          </Button>
        </Box>
      )}

      <SubmitCandidatesPopup
        open={showSubmitPopup}
        onClose={() => setShowSubmitPopup(false)}
        selectedCandidates={selectedCandidates}
        isSubmitting={isUpdating}
        onConfirm={handleAction}
        title={'Approve Candidates'}
        subtitle={'Review and confirm the candidates you want to approve.'}
        confirmLabel={'Approve Candidate'}
      />

      <Dialog
        open={openScoreModal}
        onClose={() => setOpenScoreModal(false)}
        fullScreen>
        <CandidateMatchScoresScreen
          isModal={true}
          onClose={() => setOpenScoreModal(false)}
          candidates={submittedCandidatesData?.data || []}
          expFrom={jobInfo?.expFrom}
          expTo={jobInfo?.expTo}
          goal={jobInfo?.goal}
          jobTitle={jobInfo?.jobTitle}
          loading={isLoadingSubmitted}
          selectedCandidateId={selectedScoreCandidateId || undefined}
        />
      </Dialog>
    </>
  );
};

export default JobContent;
